<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import VSelectInvestor from '@/modules/investing/components/VSelectInvestor.vue'
import { individual_other_entity_columns } from '@/modules/investing/config/columns'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { VBadge, VTable } from '@/modules/shared/components'
import { unflattenKey } from '@/modules/shared/utils/v-table'

const { individual_id, slug } = useRoute().params as { individual_id: string; slug: string }
const authStore = useAuthStore()
const individualStore = useIndividualStore()
const individual = computed(() => individualStore.individual)

const selectedCids = computed(() =>
  individualStore.selectedShareholderKeys.map((keys) => JSON.parse(keys).reverse().join(':')),
)

const otherEntities = computed(() => individualStore.listOtherEntities)

const isAdmin = computed(() => authStore.is_site_or_group_admin)
const is_viewing_own_profile = computed(() => individual_id === authStore.current_user.investor_id?.toString())
const is_entity_link_disabled = computed(() => !authStore.is_site_or_group_admin && !is_viewing_own_profile.value)

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetch = async (shareholder_cids = null) => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug, shareholder_cids })

  if (shareholder_cids === null)
    shareholder_cids = individualStore.listShareholders.map((shareholder) => `${shareholder.type}:${shareholder.id}`)
  await individualStore.fetchOtherEntities(individual.value.other_entity_ids, { slug, shareholder_cids })

  hideSkeleton()
}

const onFilter = async () => {
  await fetch(selectedCids.value)
}

const canEditEntity = (item) =>
  authStore.is_site_or_group_admin ||
  unflattenKey(item, 'admins')?.some(
    (admin) => admin.id?.toString() === authStore.current_user?.investor_id?.toString(),
  )

onMounted(async () => {
  await fetch()
})
</script>

<template>
  <IndividualLayout selectedTab="associated-entities">
    <VSelectInvestor
      v-model="individualStore.selectedShareholderKeys"
      :investors="individualStore.listShareholders"
      :onClose="onFilter"
    />

    <VTable
      :columns="individual_other_entity_columns"
      :items="otherEntities"
      :name="`individuals-associated-entities-Investor:${individual_id}`"
      :skeleton="skeleton"
      :slots="['admins', 'name']"
      class="mt-6"
    >
      <template #admins="{ item: entity }">
        <span v-for="(admin, index) in unflattenKey(entity, 'admins')" :key="admin.id">
          <RouterLink
            v-if="isAdmin"
            class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
            :to="{ name: 'investing.individual-overview', params: { individual_id: admin.id } }"
          >
            {{ admin.name }}
          </RouterLink>
          <span class="text-gray-900" v-else>{{ admin.name }}</span>
          <span v-if="index !== unflattenKey(entity, 'admins').length - 1">, </span>
        </span>
      </template>
      <template #name="{ item }">
        <span class="flex items-start gap-4">
          <RouterLink
            v-if="!is_entity_link_disabled"
            class="cursor-pointer whitespace-break-spaces font-medium text-[#51889B] underline decoration-[#51889B]/50"
            :to="{ name: 'investing.other-entity.overview', params: { other_entity_id: item.id } }"
          >
            {{ item.name }}
          </RouterLink>
          <span class="text-gray-900" v-else>{{ item.name }}</span>
          <RouterLink
            v-if="item.profile_status === 'incomplete' && canEditEntity(item)"
            :to="{ name: 'investing.other-entity.edit', params: { other_entity_id: item.id } }"
          >
            <VBadge color="red" size="xxs" class="w-fit cursor-pointer whitespace-nowrap">Profile Incomplete</VBadge>
          </RouterLink>
          <VBadge
            v-else-if="item.profile_status === 'incomplete'"
            color="red"
            size="xxs"
            class="w-fit whitespace-nowrap"
            >Profile Incomplete</VBadge
          >
        </span>
      </template>
    </VTable>
  </IndividualLayout>
</template>
